import React, { useEffect, useState } from "react";
import Table from "../../../../core/common/dataTable/index";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../UserPermissionsContext";
import { DateRange } from "react-date-range";
import { Spin } from "antd";
import { format } from "date-fns";

interface AgentStats {
  id: number;
  username: string;
  total_calls: number;
  total_duration: string; // Format: "HH:MM:SS"
  total_answered_calls_by_agent: number;
  total_answered_calls_by_employee: number;
  total_missed_calls_by_agent: number;
  total_missed_calls_by_employee: number;
  last_call_date: string | null; // ISO date string or null
  unique_teacher_touch: number;
}

const SedCallSummary = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState<string>("");
  const [data, setData] = useState<AgentStats[]>([]);

  const [showDateRange, setShowDateRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject,
    );
  };

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  useEffect(() => {
    if (!checkPermission("view_sed_calllogs", "calllog")) {
      navigate(routes.crmLandingPage);
      return;
    }
    fetchData();
  }, [userPermissions, currentPage, searchText, pageSize, fromDate, toDate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: String(currentPage),
        search: searchText || "",
      });
      queryParams.append("page_size", String(pageSize));
      const response = await fetch(
        `${webApi}/call/agent_wise_sed_call_analytics/?${queryParams.toString()}&from_date=${fromDate}&to_date=${toDate}`,
        options,
      );
      const result = await response.json();
      if (!response.ok) {
        if (result.code === "token_not_valid") {
          navigate(routes.crmLandingPage);
          return;
        }
        throw new Error(result.detail || "Failed to fetch teachers");
      }
      setData(result.results.data || []);
      setTotalCount(result.total_count || 0);
    } catch (error) {
      console.error("Teachers fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      title: "Agent Name ",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Unique Teacher Touch",
      dataIndex: "unique_teacher_touch",
      key: "unique_teacher_touch",
    },
    {
      title: "Total Calls",
      dataIndex: "total_calls",
      key: "total_calls",
    },
    {
      title: "Total Duration",
      dataIndex: "total_duration",
      key: "total_duration",
    },
    {
      title: "Total Answered call by Agent",
      dataIndex: "total_answered_calls_by_agent",
      key: "total_answered_calls_by_agent",
    },
    {
      title: "Total Answered calls by employee",
      dataIndex: "total_answered_calls_by_employee",
      key: "total_answered_calls_by_employee",
    },
    {
      title: "Total missed calls by agent ",
      dataIndex: "total_missed_calls_by_agent",
      key: "total_missed_calls_by_agent",
    },
    {
      title: "Total missed calls by employee",
      dataIndex: "total_missed_calls_by_employee",
      key: "total_missed_calls_by_employee",
    },
    {
      title: "Last Call Date",
      dataIndex: "last_call_date",
      key: "last_call_date",
    },
  ];

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };
  const handleExportClick = () => {
    let url = `${webApi}/call/export_agent_wise_sed_call_analytics/?token=${jwtToken}&from_date=${fromDate}&to_date=${toDate}`;
    window.location.href = url;
  };

  const handleSelect = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;
    setSelectionRange(ranges.selection);
  };

  const handleApplyDateRange = () => {
    setFromDate(format(selectionRange.startDate, "yyyy-MM-dd"));
    setToDate(format(selectionRange.endDate, "yyyy-MM-dd"));

    setCurrentPage(1);
    setShowDateRange(false);
  };

  const handleResetDateRange = () => {
    setSelectionRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    });

    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setShowDateRange(false);
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">SED Call Summary</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.SEDDashBoard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Call Summary
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                {checkPermission("view_sed_call_summary", "calllog") && (
                  <button
                    className="btn btn-primary me-2"
                    onClick={handleExportClick}
                  >
                    Export
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Call Logs List</h4>

              <div className="d-flex align-items-center gap-2 mb-3 position-relative">
                {/* Date Range Button */}
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={() => setShowDateRange(!showDateRange)}
                >
                  <i className="bi bi-calendar3"></i>
                  {fromDate && toDate
                    ? `${fromDate} - ${toDate}`
                    : "Date Range"}
                </button>

                {/* Calendar Popup */}
                {showDateRange && (
                  <div
                    className="position-absolute bg-white shadow rounded border"
                    style={{
                      top: "45px",
                      right: "110px", // adjust if needed
                      zIndex: 1050,
                    }}
                  >
                    <DateRange
                      ranges={[selectionRange]}
                      onChange={handleSelect}
                      moveRangeOnFirstSelection={false}
                      editableDateInputs
                      rangeColors={["#0d6efd"]}
                    />
                    <div className="d-flex justify-content-end gap-2 p-3 border-top">
                      <button
                        className="btn btn-outline-danger"
                        onClick={handleResetDateRange}
                      >
                        Reset
                      </button>

                      <button
                        className="btn btn-primary"
                        onClick={handleApplyDateRange}
                      >
                        Apply
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowDateRange(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={data}
                  Selection={false}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: ["10", "25", "100", "500", "1000"],
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                  searchText={searchText}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
    </div>
  );
};

export default SedCallSummary;
